/**
 * Auth surfaces shell — header, split-column main, footer.
 *
 * Wraps every state of the auth gate (signed-out, unverified, resolving)
 * in a consistent editorial frame:
 *   - Top: Greenbroad wordmark + a quiet sign-in/sign-out link
 *   - Main: a two-column grid on desktop (form left, "what's next"
 *     side panel right). Stacks on mobile.
 *   - Bottom: trust signals + legal microcopy
 *
 * The right-rail panel content is passed in via `sidePanel` so each
 * auth state can present its own journey context (the unverified
 * prompt's panel is different from the sign-up panel, etc.).
 */
import type { ReactNode } from 'react';

import styles from './AuthLayout.module.css';

export interface IAuthLayoutProps {
  /** Eligibility/context chip rendered just above the headline. */
  eligibilityBadge?: ReactNode;
  /** Top-right link copy + handler (e.g., "Already have an account?"). */
  headerLink?: {
    label: string;
    onClick: () => void;
  };
  /** The primary form area (left column on desktop). */
  children: ReactNode;
  /** The journey panel rendered to the right (or below on mobile). */
  sidePanel?: ReactNode;
  /**
   * When true, render the form column as a single centered card
   * (used by the sign-in surface — returning users don't need the
   * journey panel scaffolding). When false, the two-column grid
   * spreads the form left + side panel right.
   */
  centered?: boolean;
  /** Privacy-page handler — wires the "privacy practices" link. */
  onShowPrivacy?: () => void;
}

export function AuthLayout({
  eligibilityBadge,
  headerLink,
  children,
  sidePanel,
  centered = false,
  onShowPrivacy,
}: IAuthLayoutProps): JSX.Element {
  return (
    <div className={styles.shell}>
      {/* Soft background ornament — a single asymmetric blur disc in
          the top-right. Adds depth without competing with the form;
          aria-hidden so screen readers skip it. */}
      <div className={styles.shell__ornament} aria-hidden="true" />

      <header className={styles.header}>
        <a href="/" className={styles.header__brand} aria-label="Greenbroad home">
          <img
            src="/brand/greenbroad-wordmark.png"
            alt="Greenbroad"
            width={148}
            height={31}
          />
        </a>
        {headerLink ? (
          <button
            type="button"
            className={styles.header__link}
            onClick={headerLink.onClick}
          >
            {headerLink.label} →
          </button>
        ) : null}
      </header>

      <main id="main-content" className={styles.main}>
        <div
          className={`${styles.grid} ${centered ? styles['grid--centered'] : ''}`}
        >
          <section className={styles.form_column} aria-label="Account">
            {eligibilityBadge ? (
              <div className={styles.badge_row}>{eligibilityBadge}</div>
            ) : null}
            {children}
          </section>
          {sidePanel ? (
            <aside
              className={styles.side_column}
              aria-label="What happens next"
            >
              {sidePanel}
            </aside>
          ) : null}
        </div>
      </main>

      <footer className={styles.footer}>
        <p className={styles.footer__legal}>
          By creating an account you agree to our{' '}
          {onShowPrivacy ? (
            <button
              type="button"
              className={styles.footer__link}
              onClick={onShowPrivacy}
            >
              privacy practices
            </button>
          ) : (
            <a href="/privacy" className={styles.footer__link}>
              privacy practices
            </a>
          )}
          . Greenbroad helps you prepare USCIS forms — we are{' '}
          <strong>not a law firm</strong> and do not provide legal advice.
        </p>
        <div className={styles.footer__signals}>
          <span className={styles.footer__signal}>
            <span className={styles.footer__signal_dot} aria-hidden="true" />
            Bank-level encryption
          </span>
          <span className={styles.footer__signal}>
            <span className={styles.footer__signal_dot} aria-hidden="true" />
            Human review on every case
          </span>
          <span className={styles.footer__signal}>
            <span className={styles.footer__signal_dot} aria-hidden="true" />
            Save + return on any device
          </span>
        </div>
      </footer>
    </div>
  );
}

/**
 * Reusable eligibility chip — small pill the AuthLayout renders
 * above the headline when sign-up follows a successful screener
 * outcome.
 */
export function EligibilityBadge(props: { label: string }): JSX.Element {
  return (
    <span className={styles.eligibility_chip} data-eligibility-chip>
      <span className={styles.eligibility_chip__check} aria-hidden="true">
        ✓
      </span>
      {props.label}
    </span>
  );
}
